// Plan-gating UI: FeatureGate wraps a premium surface and shows an upgrade
// upsell instead when the tenant's tier doesn't include it. ReadOnlyBanner
// surfaces the backend's past_due/suspended read-only state. Both read the
// same entitlements the backend enforces (use_entitlements → me.entitlements).

import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';
import { use_language } from '../i18n/index.js';
import { use_entitlements, min_tier_for } from '../lib/entitlements.js';
import { Icon } from './Icon.js';

const BILLING_HREF = '/settings?tab=billing';

const TIER_LABEL: Record<string, { en: string; ta: string }> = {
  growth: { en: 'Growth', ta: 'வளர்ச்சி' },
  scale: { en: 'Scale', ta: 'அளவீடு' },
};

/** Render `children` when the tenant's plan includes `feature`; otherwise show a
 *  compact upsell pointing at the billing page. */
export function FeatureGate({
  feature,
  title_en,
  title_ta,
  children,
}: {
  feature: string;
  title_en: string;
  title_ta: string;
  children: ReactNode;
}): ReactElement {
  const lang = use_language();
  const ta = lang === 'ta';
  const { has_feature, is_loading } = use_entitlements();
  if (is_loading || has_feature(feature)) return <>{children}</>;

  const tier = TIER_LABEL[min_tier_for(feature)] ?? TIER_LABEL.growth!;
  const title = ta ? title_ta : title_en;
  return (
    <div className="rounded-xl border border-line bg-bg-elev p-8 text-center max-w-md mx-auto my-10">
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/10 text-primary mb-4">
        <Icon.shield />
      </div>
      <h3 className={'text-base font-semibold text-ink ' + (ta ? 'tamil' : '')}>{title}</h3>
      <p className={'mt-2 text-sm text-ink/60 ' + (ta ? 'tamil' : '')}>
        {ta
          ? `இந்த அம்சம் ${tier.ta} திட்டத்தில் கிடைக்கும்.`
          : `This feature is available on the ${tier.en} plan.`}
      </p>
      <Link
        to={BILLING_HREF}
        className={
          'mt-5 inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-primary hover:bg-primary-2 text-white text-sm font-medium no-underline ' +
          (ta ? 'tamil' : '')
        }
      >
        {ta ? `${tier.ta} திட்டத்திற்கு மேம்படுத்து` : `Upgrade to ${tier.en}`}
      </Link>
    </div>
  );
}

/** Sticky-ish banner shown above page content when the workspace is read-only
 *  (past_due / suspended) — mirrors the backend require_active_write guard. */
export function ReadOnlyBanner(): ReactElement | null {
  const lang = use_language();
  const ta = lang === 'ta';
  const { is_read_only, entitlements } = use_entitlements();
  if (!is_read_only) return null;
  const past_due = entitlements?.status === 'past_due';
  return (
    <div className="mx-6 mt-4 rounded-lg border border-warn/40 bg-warn-soft px-4 py-3 flex items-center justify-between gap-4">
      <div className={'text-sm text-warn ' + (ta ? 'tamil' : '')}>
        {past_due
          ? ta
            ? 'சந்தா நிலுவையில் உள்ளது — புதுப்பிக்கும் வரை வேலையகம் read-only.'
            : 'Subscription past due — your workspace is read-only until you renew.'
          : ta
            ? 'வேலையகம் இடைநிறுத்தப்பட்டுள்ளது. ஆதரவைத் தொடர்பு கொள்ளுங்கள்.'
            : 'This workspace is suspended. Please contact support.'}
      </div>
      {past_due && (
        <Link
          to={BILLING_HREF}
          className={'shrink-0 px-3 py-1.5 rounded bg-warn text-white text-xs font-medium no-underline ' + (ta ? 'tamil' : '')}
        >
          {ta ? 'புதுப்பி' : 'Renew'}
        </Link>
      )}
    </div>
  );
}
